import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Link from '@mui/material/Link'
import Grid from '@mui/material/Grid2'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { authAPI } from 'api/AuthAPI'
import { termsAndPolicyApi } from 'api/TermsAndPolicyAPI'
import ErrorOverlay from 'Components/General/ErrorOverlay'
import CaleoPrimaryButton from 'Components/reusable/Buttons/CaleoPrimaryButton'
import CardContentText from 'Components/reusable/CaleoCustomComponents/CardContentText'
import LoginPageCard from 'Components/reusable/CaleoCustomComponents/LoginPageCard'
import ModalPaper from 'Components/reusable/CaleoCustomComponents/ModalPaper'
import { useNotificationPopup } from 'Components/reusable/Notification'
import { useLogin } from 'hooks'
import i18n from 'i18next'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router'
import { IError } from 'types/error'
import { ITermsAndPolicy } from 'types/termsAndPolicyInterfaces'
import { chooseDBTranslation } from 'utils/translations'
import TermsAndPolicyModal from 'Components/reusable/TermsAndPolicyModal/TermsAndPolicyModal'
import { IActivationData } from 'types/accountInterfaces'
import LoadingIndicator from 'Components/reusable/LoadingIndicator'
import caleoLogo from 'assets/images/caleoLogo.svg'

/**
 * Renders the ActivateAccount component.
 *
 * @return {ReactNode} The rendered ReactNode.
 * @notExported
 */
const ActivateAccount: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { setNotificationPopup } = useNotificationPopup()
  const { doLogin, getAzureUser } = useLogin()

  const [termsAndPolicies, setTermsAndPolicies] = useState<ITermsAndPolicy[]>()
  const [acceptedTermsAndPolicies, setAcceptedTermsAndPolicies] = useState<number[]>([])
  const [buttonsEnabled, setButtonsEnabled] = useState<boolean>(true)
  const [error, setError] = useState<string>('')
  const [backendError, setBackendError] = useState<IError>()
  const [selectedItem, setSelectedItem] = useState<ITermsAndPolicy | null | undefined>(undefined)
  const [loading, setLoading] = useState<boolean>(true)

  useEffect(() => {
    const controller = new AbortController()
    ;(async () => {
      try {
        setLoading(true)
        if (
          location.state &&
          location.state?.userData?.accountId &&
          location.state?.password &&
          location.state?.token
        ) {
          const termsAndPolicies = await termsAndPolicyApi.getGlobalTermsAndPolicies(controller)
          setTermsAndPolicies(termsAndPolicies)
        } else if (location.state && location.state?.termsNotAccepted && location.state?.userData?.userId) {
          const termsAndPolicies = await termsAndPolicyApi.getGlobalTermsAndPolicies(controller)
          const userTermsAndPolicies = await termsAndPolicyApi.getUsersTermsAndPolicies(
            location.state?.userData?.userId,
            controller
          )
          const unacceptedPolicies = termsAndPolicies.filter(item => {
            if (userTermsAndPolicies.length === 0) return true

            return !userTermsAndPolicies.find(({ termsAndPolicyId }) => item.id === termsAndPolicyId)
          })
          setTermsAndPolicies(unacceptedPolicies)
        } else {
          navigate('/login')
        }
      } catch (error) {
        setBackendError(error as IError)
      } finally {
        setLoading(false)
      }
    })()

    return () => {
      controller.abort()
    }
  }, [location, navigate])

  const handleTermsAndPolicyAcceptedChange = (termsAndPolicyId: number) => {
    const index = acceptedTermsAndPolicies.indexOf(termsAndPolicyId)
    if (index < 0) {
      setAcceptedTermsAndPolicies([...acceptedTermsAndPolicies, termsAndPolicyId])
    } else {
      setAcceptedTermsAndPolicies(acceptedTermsAndPolicies.filter(accepted => accepted !== termsAndPolicyId))
    }
  }

  const handleSave = async () => {
    try {
      setButtonsEnabled(false)
      if (location.state && location.state?.termsNotAccepted && location.state?.loginDetails) {
        const acceptanceData = {
          acceptances: acceptedTermsAndPolicies,
        }
        const accountId = location.state?.userData?.userId
        await termsAndPolicyApi.updateAcceptances(accountId, acceptanceData)
        if (location.state?.loginDetails?.azure) {
          await getAzureUser()
        } else {
          await doLogin(location.state?.loginDetails)
        }
        navigate('/profile')
      } else {
        const acceptanceData = {
          acceptances: acceptedTermsAndPolicies,
        }
        const activationData: IActivationData = {
          password: location.state?.password,
          token: location.state?.token,
        }
        const accountId = location.state?.userData?.accountId

        await termsAndPolicyApi.updateAcceptances(accountId, acceptanceData)
        await authAPI.activate(activationData)

        setNotificationPopup({
          message: t('accountActivatedMessage'),
          type: 'success',
          duration: 'long',
        })
        navigate('/login')
      }
    } catch (error) {
      const err = error as IError
      setButtonsEnabled(true)
      if (err.response?.status === 400 && err.response?.data === 'same-password-error') {
        navigate(`/confirm/${location.state?.token}`, { state: { error: err.response?.data } })
      } else {
        setError(err.message ?? '')
        setBackendError(error as IError)
      }
    }
  }

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} />
  }

  if (loading || !termsAndPolicies) {
    return <LoadingIndicator />
  }

  let acceptanceMissing = false
  termsAndPolicies.forEach(item => {
    if (acceptedTermsAndPolicies.indexOf(item.id) < 0) {
      acceptanceMissing = true
    }
  })

  return (
    <Grid container justifyContent="center" alignItems="center">
      <Grid size={12}>
        <img src={caleoLogo} alt="logo" style={{ width: '159px', padding: '18px' }} />
      </Grid>
      <Grid container justifyContent="center" alignItems="center" size={12}>
        <Grid size={{ xs: 12, sm: 8, md: 6, lg: 4, xl: 3 }}>
          <LoginPageCard header={t('password.activateAccount')} avatar={<CheckCircleIcon />}>
            <Grid container justifyContent="flex-start">
              <Grid style={{ marginTop: '16px', marginBottom: '16px' }} size={12}>
                <ModalPaper>
                  <CardContentText type="header">{t('acceptFollowingTerms')}:</CardContentText>
                  {termsAndPolicies.map(item => {
                    return (
                      <div key={item.id}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              data-testid={`checkbox-activate-account-terms-${item.id}`}
                              checked={acceptedTermsAndPolicies.includes(item.id)}
                              onChange={() => handleTermsAndPolicyAcceptedChange(item.id)}
                              name="checkbox"
                              color="secondary"
                            />
                          }
                          label={
                            <Link
                              href="#"
                              onClick={e => {
                                e.preventDefault()
                                setSelectedItem(item)
                              }}
                            >
                              {chooseDBTranslation(i18n, item).title}
                            </Link>
                          }
                        />
                      </div>
                    )
                  })}
                  <CardContentText type="header">{t('acceptFollowingTerms.instructions')}</CardContentText>
                </ModalPaper>
              </Grid>
            </Grid>
            <Grid container justifyContent="flex-end">
              <CardContentText type="error">{error}</CardContentText>
            </Grid>
            <Grid container justifyContent="flex-end">
              <CaleoPrimaryButton
                testId="button-activate-account-submit"
                clickAction={handleSave}
                label={t('password.activate')}
                valid={!buttonsEnabled || !acceptanceMissing}
              />
            </Grid>
          </LoginPageCard>
        </Grid>
      </Grid>
      {selectedItem !== undefined && (
        <TermsAndPolicyModal
          item={selectedItem}
          onClose={() => setSelectedItem(undefined)}
          header={chooseDBTranslation(i18n, selectedItem)?.title}
          noEscClose
        />
      )}
    </Grid>
  )
}

export default ActivateAccount
